import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";
import { Star, Minus, Plus, ShoppingBag, Heart, MessageCircle, Truck } from "lucide-react";

function ShippingNote({ sellerId }) {
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => {
    api.get(`/sellers/${sellerId}`).then(({ data }) => setInfo(data)).catch(() => {});
  }, [sellerId]);
  if (!info) return null;
  const dom = Number(info.shipping_domestic || 0);
  const intl = Number(info.shipping_international || 0);
  const thresh = Number(info.free_shipping_threshold || 0);
  return (
    <div className="mt-5 border border-border rounded-sm p-4 bg-secondary/30">
      <div className="flex items-start gap-3 text-sm">
        <Truck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-muted-foreground leading-relaxed">
          <span className="text-foreground font-medium">
            {dom > 0 ? `$${dom.toFixed(2)} domestic shipping` : "Free domestic shipping"}
          </span>
          {thresh > 0 && (
            <> · <span className="text-primary">Free over ${thresh.toFixed(0)}</span></>
          )}
          {info.ships_internationally && intl > 0 && (
            <> · ${intl.toFixed(2)} international</>
          )}
          {!info.ships_internationally && <> · Domestic only</>}
        </div>
      </div>
    </div>
  );
}

const resolveImg = (img) =>
  img?.startsWith("http") ? img : `${API}/files/${img}`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const { has, toggle } = useWishlist();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [selected, setSelected] = useState({});

  const load = async () => {
    const { data } = await api.get(`/products/${id}`);
    setP(data);
    setSelected({});
  };
  useEffect(() => { load(); }, [id]);

  const hasVariants = () => Boolean(p?.options?.length && p?.variants?.length);
  const currentVariant = () =>
    hasVariants() ? p.variants.find((v) => p.options.every((o) => selected[o.name] === v.options[o.name])) || null : null;

  const onAdd = () => {
    if (hasVariants()) {
      const missing = p.options.filter((o) => !selected[o.name]).map((o) => o.name.toLowerCase());
      if (missing.length) {
        toast.error(`Please select a ${missing.join(" and ")} before adding this item to your cart.`);
        return false;
      }
      const v = currentVariant();
      if (!v || Number(v.stock) <= 0) { toast.error("Sorry — that combination is sold out."); return false; }
      add(p, qty, {
        id: v.id,
        price: v.price,
        image: v.image || p.images?.[0] || "",
        label: p.options.map((o) => `${o.name}: ${selected[o.name]}`).join(", "),
      });
      toast.success(`Added "${p.title}" to your bag`);
      return true;
    }
    add(p, qty);
    toast.success(`Added "${p.title}" to your bag`);
    return true;
  };

  const onBuyNow = () => { if (onAdd()) navigate("/cart"); };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    try {
      await api.post(`/products/${id}/reviews`, review);
      setReview({ rating: 5, comment: "" });
      await load();
      toast.success("Thank you for your review.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading…</div>;

  const variantMode = Boolean(p.options?.length && p.variants?.length);
  const curVar = variantMode ? p.variants.find((v) => p.options.every((o) => selected[o.name] === v.options[o.name])) || null : null;
  const variantPrices = variantMode ? p.variants.map((v) => Number(v.price)) : [];
  const minVarPrice = variantPrices.length ? Math.min(...variantPrices) : 0;
  const maxVarPrice = variantPrices.length ? Math.max(...variantPrices) : 0;
  const availStock = variantMode ? (curVar ? Number(curVar.stock) : (Number(p.stock) > 0 ? 1 : 0)) : Number(p.stock);
  const mainImg = (variantMode && curVar?.image) ? curVar.image : p.images?.[activeImg];
  // a value is fully unavailable if every variant containing it is out of stock
  const valueSoldOut = (optName, val) =>
    variantMode && p.variants.filter((v) => v.options[optName] === val).every((v) => Number(v.stock) <= 0);

  return (
    <div data-testid="product-detail" className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Images */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden bg-secondary rounded-sm grain">
            <img src={resolveImg(mainImg)} alt={p.title} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {p.images.map((im, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className={`aspect-square overflow-hidden rounded-sm border ${activeImg === idx ? "border-primary" : "border-border/40"}`}
                >
                  <img src={resolveImg(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Info */}
        <div className="lg:col-span-5 lg:pl-8 lg:pt-4">
          <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground">◦ {p.category}</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mt-4 leading-tight">{p.title}</h1>
          <Link to={`/shop/${p.seller_id}`} data-testid="seller-link" className="mt-2 inline-block text-sm text-muted-foreground hover:text-primary transition-colors">
            By <span className="underline underline-offset-4 decoration-1">{p.seller_name}</span>
            {p.is_founding_seller && <span className="founding-badge text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-0.5 rounded-sm ml-2">◦ Founding</span>}
          </Link>
          {p.rating_count > 0 && (
            <div className="mt-3 flex items-center gap-1 text-sm">
              <Star className="w-4 h-4 fill-primary text-primary" />
              {p.rating_avg.toFixed(1)} <span className="text-muted-foreground">({p.rating_count})</span>
            </div>
          )}
          <div className="font-serif text-3xl mt-8" data-testid="product-price">
            {variantMode
              ? (curVar
                  ? `$${Number(curVar.price).toFixed(2)}`
                  : (minVarPrice === maxVarPrice ? `$${minVarPrice.toFixed(2)}` : `from $${minVarPrice.toFixed(2)}`))
              : `$${Number(p.price).toFixed(2)}`}
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">{p.description}</p>

          {/* Option selectors */}
          {variantMode && (
            <div className="mt-8 space-y-6" data-testid="variant-options">
              {p.options.map((opt) => (
                <div key={opt.name} data-testid={`option-group-${opt.name}`}>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">
                    Choose {opt.name}
                    {selected[opt.name] && <span className="text-foreground normal-case tracking-normal ml-2">· {selected[opt.name]}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {opt.values.map((val) => {
                      const active = selected[opt.name] === val;
                      const dead = valueSoldOut(opt.name, val);
                      return (
                        <button
                          key={val}
                          type="button"
                          disabled={dead}
                          data-testid={`option-${opt.name}-${val}`}
                          onClick={() => setSelected((s) => ({ ...s, [opt.name]: val }))}
                          className={`px-4 py-2.5 text-sm rounded-sm border transition-colors ${
                            active
                              ? "border-foreground bg-foreground text-background"
                              : dead
                                ? "border-border/50 text-muted-foreground/50 line-through cursor-not-allowed"
                                : "border-border hover:border-primary"
                          }`}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border border-border rounded-sm">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-secondary transition-colors"><Minus className="w-3.5 h-3.5" /></button>
              <span className="px-4 text-sm w-8 text-center">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="p-3 hover:bg-secondary transition-colors"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={onAdd}
              disabled={availStock <= 0}
              className="flex-1 border border-foreground py-3.5 text-sm tracking-wide hover:bg-foreground hover:text-background transition-colors disabled:opacity-50 rounded-sm"
            >
              {availStock > 0 ? "Add to bag" : "Sold out"}
            </button>
            <button
              data-testid="wishlist-detail-btn"
              onClick={() => toggle(p.id)}
              aria-label="Add to wishlist"
              className="border border-border w-12 h-12 flex items-center justify-center hover:border-primary transition-colors rounded-sm"
            >
              <Heart className={`w-4 h-4 ${has(p.id) ? "fill-primary text-primary" : ""}`} />
            </button>
          </div>
          <button
            data-testid="buy-now-btn"
            onClick={onBuyNow}
            disabled={availStock <= 0}
            className="btn-primary w-full mt-3 py-3.5 text-sm tracking-wide rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <ShoppingBag className="w-4 h-4" /> Buy now
          </button>
          <Link
            to={`/shop/${p.seller_id}`}
            data-testid="visit-seller-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm border border-border py-3 hover:border-primary hover:text-primary transition-colors rounded-sm"
          >
            <MessageCircle className="w-4 h-4" /> Visit shop &amp; message seller
          </Link>
          <div className="mt-6 text-xs text-muted-foreground">
            {availStock > 0 ? `${availStock} available · ` : ""}Made to order · Ships in 5–7 days
          </div>
          {/* Shipping note */}
          <ShippingNote sellerId={p.seller_id} />
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ Notes</div>
          <h2 className="font-serif text-3xl lg:text-4xl tracking-tight">From the people who own one.</h2>
          {user && (
            <form onSubmit={submitReview} className="mt-8 border border-border/40 p-6 rounded-sm">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setReview({ ...review, rating: n })}>
                    <Star className={`w-5 h-5 ${n <= review.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                  </button>
                ))}
              </div>
              <textarea
                data-testid="review-input"
                value={review.comment}
                onChange={(e) => setReview({ ...review, comment: e.target.value })}
                placeholder="Share a note about this piece…"
                rows={4}
                className="w-full bg-transparent border border-border/40 p-3 text-sm rounded-sm focus:outline-none focus:border-primary"
              />
              <button data-testid="submit-review-btn" className="btn-primary mt-4 px-5 py-2.5 text-sm tracking-wide rounded-sm">Post note</button>
            </form>
          )}
        </div>
        <div className="lg:col-span-7 space-y-6">
          {(p.reviews || []).length === 0 ? (
            <div className="text-muted-foreground italic">No notes yet — be the first.</div>
          ) : p.reviews.map((r) => (
            <div key={r.id} className="border-b border-border/40 pb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">{r.user_name}</div>
                <div className="flex gap-0.5">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />)}
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{r.comment}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
